import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLang } from "../../contexts/LanguageContext";
import './Transcription.css';

const convertirAEnlacesJSX = (lemmatizationArray, phonologizationArray, handleWordClick, currentWordId, linkIdCounter) => {
  let elements = [];
  let currentParagraph = [];
  let currentTag = 'paragraph';

  const closeParagraph = (tag, key) => {
    if (currentParagraph.length === 0) return;

    const tagMap = {
        't': 'title',
        's': 'subtitle',
        'r': 'resume',
        'v': 'stanza'
    };
    const className = tagMap[tag] || 'paragraph';

    elements.push(
      <p className={className} key={key}>
        {currentParagraph}
      </p>
    );
    currentParagraph = [];
  };

  lemmatizationArray.forEach((item, i) => {
    if (Array.isArray(item)) {
      const [word, lemma, pos, meta] = item;
      const phonology = phonologizationArray[i] && phonologizationArray[i][0] ? phonologizationArray[i][0] : '';
      
      // Lógica de detección de etiqueta: usa la actual, o intenta heredar de vecinos si es espacio/punct
      let tag = meta?.tag;
      if (!tag) {
          const prev = lemmatizationArray[i-1];
          const next = lemmatizationArray[i+1];
          if (prev && Array.isArray(prev) && prev[3]?.tag) tag = prev[3].tag;
          else if (next && Array.isArray(next) && next[3]?.tag) tag = next[3].tag;
          else tag = 'paragraph';
      }

      // Si la etiqueta cambia (o iniciamos), cerramos el párrafo anterior
      if (tag !== currentTag && currentParagraph.length > 0) {
          closeParagraph(currentTag, i);
          currentTag = tag;
      }

      if (pos === 'space') {
        if (word === '\n' || word === '\r\n' || word === ' \n') {
          currentParagraph.push(<br key={i} />);
        } else if (word === '\n\n' || word === '\r\n\r\n') {
           // Ya gestionado por closeParagraph
        } else {
          currentParagraph.push(word);
        }
      } else if (pos === 'punct') {
        currentParagraph.push(word);
      } else {
        const anchor = (
          <a
            className={`mostrarModal ${linkIdCounter === currentWordId ? 'current-word' : ''}`}
            href="#"
            key={linkIdCounter}
            id={linkIdCounter}
            onClick={(e) => {
              e.preventDefault();
              handleWordClick(lemma, pos, linkIdCounter);
            }}
          >
            {word}
            <span className="p">{pos}</span>
            <span className="fon" style={{ display: 'none' }}>{phonology}</span>
          </a>
        );

        currentParagraph.push(anchor);
        linkIdCounter++;
      }
      
      currentTag = tag;
    } else {
      currentParagraph.push(item);
    }
  });

  closeParagraph(currentTag, 'final');
  return elements;
};

// Transcription component
const Transcription = ({ lemmatizationArray, phonologizationArray, handleWordClick, currentWordId, fontSize, showLemma }) => {
  const { t } = useLang();
  const containerRef = useRef(null);
  const [linkIdCounter, setLinkIdCounter] = useState(1);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const autoScrollRef = useRef(true);
  const suppressNextScrollRef = useRef(false);
  const suppressTimeoutRef = useRef(null);
  const lastLineTopRef = useRef(null);

  useEffect(() => {
    autoScrollRef.current = autoScrollEnabled;
    if (!autoScrollEnabled) {
      lastLineTopRef.current = null;
    }
  }, [autoScrollEnabled]);

  useEffect(() => {
    // Reset linkIdCounter when lemmatizationArray changes
    setLinkIdCounter(1);
  }, [lemmatizationArray]);

  useEffect(() => {
    const transcription = document.getElementById("transcription");
    const sizeClasses = ["uTextA", "uTextB", "uTextC", "uTextD", "uTextE"];
    transcription.classList.remove(...sizeClasses);
    transcription.classList.add(sizeClasses[fontSize]);
  }, [fontSize]);

  useEffect(() => {
    const phonologySpans = document.querySelectorAll(".fon");
    phonologySpans.forEach(span => {
      span.style.display = showLemma ? 'inline' : 'none';
    });
  }, [showLemma]);

  useEffect(() => {
    if (!autoScrollRef.current) return;
    if (!currentWordId || currentWordId === -1) return;
    const currentAnchor = document.getElementById(String(currentWordId));
    if (!currentAnchor) return;

    const anchorRect = currentAnchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const computedStyles = window.getComputedStyle(currentAnchor);
    const parsedLineHeight = parseFloat(computedStyles.lineHeight);
    const lineHeight = Number.isFinite(parsedLineHeight) && parsedLineHeight > 0
      ? parsedLineHeight
      : Math.max(anchorRect.height || 0, 20);
    const lastLineTop = lastLineTopRef.current;

    const lineThreshold = lineHeight * 0.75;
    const lineChanged = lastLineTop !== null && Math.abs(anchorRect.top - lastLineTop) >= lineThreshold;

    const ensureVisible = () => {
      const topThreshold = 80;
      const bottomThreshold = 80;
      const needsScroll =
        anchorRect.top < topThreshold || anchorRect.bottom > viewportHeight - bottomThreshold;

      if (needsScroll) {
        suppressNextScrollRef.current = true;
        if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
        const target = window.scrollY + anchorRect.top - (viewportHeight / 2);
        window.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
        suppressTimeoutRef.current = setTimeout(() => {
          suppressNextScrollRef.current = false;
        }, 350);
        return viewportHeight / 2;
      }
      return anchorRect.top;
    };

    if (lineChanged) {
      const direction = anchorRect.top > lastLineTop ? 1 : -1;
      suppressNextScrollRef.current = true;
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
      window.scrollBy({ top: direction * lineHeight, behavior: 'smooth' });
      suppressTimeoutRef.current = setTimeout(() => {
        suppressNextScrollRef.current = false;
      }, 250);
      lastLineTopRef.current = Math.round(anchorRect.top - direction * lineHeight);
      return;
    }

    const updatedTop = ensureVisible();

    lastLineTopRef.current = Math.round(updatedTop);
  }, [currentWordId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleUserScroll = (event) => {
      if (!event) return;
      if (!event.isTrusted) return; // ignora scroll programático
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    const handleWheel = () => {
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    const handleTouchMove = () => {
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    container.addEventListener('scroll', handleUserScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('scroll', handleUserScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleUserScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('scroll', handleUserScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
    };
  }, []);

  const handleEnableAutoScroll = useCallback(() => {
    lastLineTopRef.current = null;
    setAutoScrollEnabled(true);
    const currentAnchor = currentWordId ? document.getElementById(String(currentWordId)) : null;
    if (currentAnchor) {
      suppressNextScrollRef.current = true;
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
      currentAnchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      suppressTimeoutRef.current = setTimeout(() => {
        suppressNextScrollRef.current = false;
      }, 400);
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      lastLineTopRef.current = Math.round(viewportHeight / 2);
    }
  }, [currentWordId]);

  const transcriptionContent = convertirAEnlacesJSX(
    lemmatizationArray,
    phonologizationArray,
    handleWordClick,
    currentWordId,
    linkIdCounter
  );

  return (
    <div id="transcription" ref={containerRef}>
      {transcriptionContent}
      {!autoScrollEnabled && (
        <div className="autoscroll-toggle">
          <button type="button" className="autoscroll-button" onClick={handleEnableAutoScroll}>
            {t('CHAPTER_AUTOSCROLL_RESUME')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Transcription;
